<template>
  <div class="app-container">
    <div class="sidebar">
      <SearchSidebar @filter="filterCars" />
    </div>
    <div class="main-content">
      <div class="car-listing" v-for="car in filteredCars" :key="car.id">
        <canvas
          ref="canvas"
          class="car-image"
          :data-src="car.image"
          :alt="`${car.make} ${car.model}`"
          @click="openModal(car.image)"
        ></canvas>
        <div class="car-details">
          <h2>{{ car.make }} {{ car.model }}</h2>
          <p>Price: ${{ car.price }}</p>
          <p>Year: {{ car.year }}</p>
          <p>Mileage: {{ car.mileage }} miles</p>
          <p>Fuel Type: {{ car.fuelType }}</p>
        </div>
      </div>
    </div>
    <div v-if="isModalOpen" class="modal" @click="closeModal">
      <div class="modal-content" @click.stop>
        <img :src="modalImage" alt="Car Image" />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue';
import SearchSidebar from './SearchSidebar.vue';

export default {
  name: 'App',
  components: { SearchSidebar },
  setup() {
    const cars = ref([
      {
        id: 1,
        make: 'Toyota',
        model: 'Camry',
        price: 20000,
        year: 2019,
        mileage: 30000,
        fuelType: 'Gasoline',
        image: '/static/images/cars/toyota_camry.jpg',
      },
      {
        id: 2,
        make: 'Honda',
        model: 'Civic',
        price: 18000,
        year: 2020,
        mileage: 25000,
        fuelType: 'Gasoline',
        image: '/static/images/cars/honda_civic.jpg',
      },
      {
        id: 3,
        make: 'Ford',
        model: 'Focus',
        price: 15000,
        year: 2018,
        mileage: 40000,
        fuelType: 'Diesel',
        image: '/static/images/cars/ford_focus.jpg',
      },
    ]);
    const filters = ref({});
    const filteredCars = computed(() => {
      return cars.value.filter((car) => {
        return (
          (!filters.value.make ||
            car.make
              .toLowerCase()
              .includes(filters.value.make.toLowerCase())) &&
          (!filters.value.model ||
            car.model
              .toLowerCase()
              .includes(filters.value.model.toLowerCase())) &&
          (!filters.value.year || car.year === Number(filters.value.year)) &&
          (!filters.value.price || car.price <= Number(filters.value.price))
        );
      });
    });

    const filterCars = (newFilters) => {
      filters.value = newFilters;
    };

    const isAuthenticated = ref(false); // Replace with actual authentication logic
    const allowRegistration = ref(true); // Replace with actual registration logic

    const isModalOpen = ref(false);
    const modalImage = ref('');

    const openModal = (image) => {
      modalImage.value = image;
      isModalOpen.value = true;
    };

    const closeModal = () => {
      isModalOpen.value = false;
      modalImage.value = '';
    };

    const generateThumbnails = () => {
      nextTick(() => {
        const canvases = document.querySelectorAll('.car-image');
        canvases.forEach((canvas) => {
          const imageSrc = canvas.getAttribute('data-src');
          const img = new Image();
          img.src = imageSrc;
          img.onload = () => {
            const ctx = canvas.getContext('2d');
            const scale = 0.2; // Scale to 20% of the original size
            const thumbnailWidth = img.width * scale;
            const thumbnailHeight = img.height * scale;
            canvas.width = thumbnailWidth;
            canvas.height = thumbnailHeight;
            ctx.drawImage(img, 0, 0, thumbnailWidth, thumbnailHeight);
          };
        });
      });
    };

    onMounted(() => {
      generateThumbnails();
    });

    return {
      cars,
      filteredCars,
      filterCars,
      isAuthenticated,
      allowRegistration,
      isModalOpen,
      modalImage,
      openModal,
      closeModal,
    };
  },
};
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&family=Montserrat:wght@500;600;700&display=swap');

body {
  font-family: 'Roboto', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  line-height: 1.6;
  color: #1a237e; // Deep blue text
  background-color: #f7fafd; // Soft white-blue background
}

.navbar {
  margin: 10px;
  padding: 10px 20px;
  background-color: #e3f2fd; // Light blue
  border-radius: 8px;
  border: 1px solid #bbdefb; // Subtle blue border
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.car-listing {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  border: 1px solid #bbdefb; // Subtle blue border
  padding: 4px 10px;
  border-radius: 8px;
  background-color: #ffffff; // White card

    .car-image {
    width: 180px;
    height: 120px;
    margin-right: 20px;
    border-radius: 8px;
    cursor: pointer;
    object-fit: cover;
    box-shadow: 0 2px 8px rgba(33, 150, 243, 0.08);
    background: #e3f2fd;
    border: 1px solid #bbdefb;
    display: block;
    }

  .car-details {
      h2 {
        margin: 0 0 10px;
        font-family: 'Montserrat', sans-serif;
        font-size: 1.5em;
        color: #1565c0; // Strong blue
        font-weight: 600;
        letter-spacing: 0.02em;
        text-shadow: 0.5px 0.5px 0px rgba(33,150,243,0.08);
      }

      p {
        margin: 5px 0;
        color: #263238; // Professional dark gray
        font-weight: 400;
        line-height: 1.5;
        letter-spacing: 0.01em;
      }
  }
}

.modal {
  background-color: rgba(33, 150, 243, 0.12);

  .modal-content {
    background-color: #ffffff;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 4px 16px rgba(33, 150, 243, 0.12);
  }
}

.sidebar {
  background-color: #e3f2fd;
  border-right: 1px solid #bbdefb;
  border-radius: 8px;
}

.bg-light {
  background-color: #e3f2fd !important;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.08);
  padding: 10px 20px;
  margin: 10px;
  border: 1px solid #bbdefb;
}

.container {
  margin: 20px auto;
  padding: 0 10px;
  box-sizing: border-box;
}

.app-container {
  display: flex;
  flex-direction: row;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  overflow-x: auto;
  margin-left: 10px;
  border-radius: 5px;
}

@media (max-width: 768px) {
  .app-container {
    flex-direction: column;
  }

  .sidebar {
    flex: 0 0 auto;
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #e4d7c5;
  }

  .main-content {
    flex: 1;
    width: 100%;
  }
}
</style>

<style lang="scss">
body {
  // background-color: #fffaf0 !important;
  background-color: white;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.navbar-brand {
  font-family: 'Montserrat', sans-serif;
  font-size: 2rem;
  font-weight: 700;
  color: #1565c0;
  letter-spacing: 0.03em;
  text-shadow: 1px 1px 1px rgba(33,150,243,0.08), -0.5px -0.5px 0px rgba(255,255,255,0.8);
  transition: all 0.3s ease;
  &:hover {
    color: #0d47a1;
  }
}

h1, h2, h3, h4, h5, h6 {
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  color: #3a2a1d;
  line-height: 1.3;
}

p, span, div, label, input, button {
  font-family: 'Roboto', sans-serif;
  line-height: 1.6;
}

// Override Bootstrap container max-width
.container {
  max-width: none !important; // Remove max-width constraint
}

// If you also want to override specific breakpoint max-widths:
@media (min-width: 576px) {
  .container { max-width: none !important; }
}
@media (min-width: 768px) {
  .container { max-width: none !important; }
}
@media (min-width: 992px) {
  .container { max-width: none !important; }
}
@media (min-width: 1200px) {
  .container { max-width: none !important; }
}
@media (min-width: 1400px) {
  .container { max-width: none !important; }
}
</style>
